import React, { useState, useRef, useEffect, useDebugValue, useMemo, useCallback } from 'react';

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}
let PropTypes;

try {
  PropTypes = require("prop-types");
} catch (e) {}

const isFunction = arg => typeof arg === "function";
/**
 * createInstance allows you to create instances of Async that are bound to a specific promise.
 * A unique instance also uses its own React context for better nesting capability.
 */


const createInstance = function createInstance() {
  let defaultProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let displayName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "Async";

  const _React$createContext = React.createContext(),
        Consumer = _React$createContext.Consumer,
        Provider = _React$createContext.Provider;

  class Async extends React.Component {
    constructor(props) {
      super(props);
      this.start = this.start.bind(this);
      this.load = this.load.bind(this);
      this.run = this.run.bind(this);
      this.cancel = this.cancel.bind(this);
      this.onResolve = this.onResolve.bind(this);
      this.onReject = this.onReject.bind(this);
      this.setData = this.setData.bind(this);
      this.setError = this.setError.bind(this);
      const promise = props.promise;
      const promiseFn = props.promiseFn || defaultProps.promiseFn;
      const initialValue = props.initialValue || defaultProps.initialValue;
      const initialError = initialValue instanceof Error ? initialValue : undefined;
      const initialData = initialError ? undefined : initialValue;
      this.mounted = false;
      this.counter = 0;
      this.args = [];
      this.abortController = {
        abort: () => {}
      };
      this.state = {
        initialValue,
        data: initialData,
        error: initialError,
        isLoading: !!promise || promiseFn && !initialValue,
        startedAt: promise ? new Date() : undefined,
        finishedAt: initialValue ? new Date() : undefined,
        counter: this.counter,
        cancel: this.cancel,
        run: this.run,
        reload: () => {
          this.load();
          this.run(...this.args);
        },
        setData: this.setData,
        setError: this.setError
      };
    }

    componentDidMount() {
      this.mounted = true;

      if (this.props.promise || !this.state.initialValue) {
        this.load();
      }
    }

    componentDidUpdate(prevProps) {
      const _this$props = this.props,
            watch = _this$props.watch,
            _this$props$watchFn = _this$props.watchFn,
            watchFn = _this$props$watchFn === void 0 ? defaultProps.watchFn : _this$props$watchFn,
            promise = _this$props.promise,
            promiseFn = _this$props.promiseFn;
      if (watch !== prevProps.watch) this.load();
      if (watchFn && watchFn(_objectSpread({}, defaultProps, this.props), _objectSpread({}, defaultProps, prevProps))) this.load();

      if (promise !== prevProps.promise) {
        if (promise) this.load();else this.cancel();
      }

      if (promiseFn !== prevProps.promiseFn) {
        if (promiseFn) this.load();else this.cancel();
      }
    }

    componentWillUnmount() {
      this.cancel();
      this.mounted = false;
    }

    start() {
      if ("AbortController" in window) {
        this.abortController.abort();
        this.abortController = new window.AbortController();
      }

      this.counter++;
      this.setState({
        isLoading: true,
        startedAt: new Date(),
        finishedAt: undefined,
        counter: this.counter
      });
    }

    load() {
      const promise = this.props.promise;

      if (promise) {
        this.start();
        return promise.then(this.onResolve(this.counter), this.onReject(this.counter));
      }

      const promiseFn = this.props.promiseFn || defaultProps.promiseFn;

      if (promiseFn) {
        this.start();
        return promiseFn(this.props, this.abortController).then(this.onResolve(this.counter), this.onReject(this.counter));
      }
    }

    run() {
      const deferFn = this.props.deferFn || defaultProps.deferFn;

      if (deferFn) {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        this.args = args;
        this.start();
        return deferFn(args, _objectSpread({}, defaultProps, this.props), this.abortController).then(this.onResolve(this.counter), this.onReject(this.counter));
      }
    }

    cancel() {
      this.counter++;
      this.abortController.abort();
      this.setState({
        isLoading: false,
        startedAt: undefined,
        counter: this.counter
      });
    }

    onResolve(counter) {
      return data => {
        if (this.mounted && this.counter === counter) {
          const onResolve = this.props.onResolve || defaultProps.onResolve;
          this.setData(data, () => onResolve && onResolve(data));
        }

        return data;
      };
    }

    onReject(counter) {
      return error => {
        if (this.mounted && this.counter === counter) {
          const onReject = this.props.onReject || defaultProps.onReject;
          this.setError(error, () => onReject && onReject(error));
        }

        return error;
      };
    }

    setData(data, callback) {
      this.setState({
        data,
        error: undefined,
        isLoading: false,
        finishedAt: new Date()
      }, callback);
      return data;
    }

    setError(error, callback) {
      this.setState({
        error,
        isLoading: false,
        finishedAt: new Date()
      }, callback);
      return error;
    }

    render() {
      const children = this.props.children;

      if (isFunction(children)) {
        return React.createElement(Provider, {
          value: this.state
        }, children(this.state));
      }

      if (children !== undefined && children !== null) {
        return React.createElement(Provider, {
          value: this.state
        }, children);
      }

      return null;
    }

  }

  if (PropTypes) {
    Async.propTypes = {
      children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
      promise: PropTypes.instanceOf(Promise),
      promiseFn: PropTypes.func,
      deferFn: PropTypes.func,
      watch: PropTypes.any,
      watchFn: PropTypes.func,
      initialValue: PropTypes.any,
      onResolve: PropTypes.func,
      onReject: PropTypes.func
    };
  }
  /**
   * Renders only when deferred promise is pending (not yet run).
   *
   * @prop {Function|Node} children Function (passing state) or React node
   * @prop {boolean} persist Show until we have data, even while loading or when an error occurred
   */


  const Pending = (_ref) => {
    let children = _ref.children,
        persist = _ref.persist;
    return React.createElement(Consumer, null, state => {
      if (state.data !== undefined) return null;
      if (!persist && state.isLoading) return null;
      if (!persist && state.error !== undefined) return null;
      return isFunction(children) ? children(state) : children || null;
    });
  };

  if (PropTypes) {
    Pending.propTypes = {
      children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
      persist: PropTypes.bool
    };
  }
  /**
   * Renders only while loading.
   *
   * @prop {Function|Node} children Function (passing state) or React node
   * @prop {boolean} initial Show only on initial load (data is undefined)
   */


  const Loading = (_ref2) => {
    let children = _ref2.children,
        initial = _ref2.initial;
    return React.createElement(Consumer, null, state => {
      if (!state.isLoading) return null;
      if (initial && state.data !== undefined) return null;
      return isFunction(children) ? children(state) : children || null;
    });
  };

  if (PropTypes) {
    Loading.propTypes = {
      children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
      initial: PropTypes.bool
    };
  }
  /**
   * Renders only when promise is resolved.
   *
   * @prop {Function|Node} children Function (passing data and state) or React node
   * @prop {boolean} persist Show old data while loading
   */


  const Resolved = (_ref3) => {
    let children = _ref3.children,
        persist = _ref3.persist;
    return React.createElement(Consumer, null, state => {
      if (state.data === undefined) return null;
      if (!persist && state.isLoading) return null;
      if (!persist && state.error !== undefined) return null;
      return isFunction(children) ? children(state.data, state) : children || null;
    });
  };

  if (PropTypes) {
    Resolved.propTypes = {
      children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
      persist: PropTypes.bool
    };
  }
  /**
   * Renders only when promise is rejected.
   *
   * @prop {Function|Node} children Function (passing error and state) or React node
   * @prop {boolean} persist Show old error while loading
   */


  const Rejected = (_ref4) => {
    let children = _ref4.children,
        persist = _ref4.persist;
    return React.createElement(Consumer, null, state => {
      if (state.error === undefined) return null;
      if (state.isLoading && !persist) return null;
      return isFunction(children) ? children(state.error, state) : children || null;
    });
  };

  if (PropTypes) {
    Rejected.propTypes = {
      children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
      persist: PropTypes.bool
    };
  }

  Async.Pending = Pending;
  Async.Loading = Loading;
  Async.Resolved = Resolved;
  Async.Rejected = Rejected;
  Async.displayName = displayName;
  Async.Pending.displayName = `${displayName}.Pending`;
  Async.Loading.displayName = `${displayName}.Loading`;
  Async.Resolved.displayName = `${displayName}.Resolved`;
  Async.Rejected.displayName = `${displayName}.Rejected`;
  return Async;
};
var Async = createInstance();

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty$1(target, key, source[key]);
    });
  }

  return target;
}

function _defineProperty$1(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

const useAsync = (arg1, arg2) => {
  const counter = useRef(0);
  const isMounted = useRef(true);
  const lastArgs = useRef(undefined);
  const prevOptions = useRef(undefined);
  const abortController = useRef({
    abort: () => {}
  });
  const options = typeof arg1 === "function" ? _objectSpread$1({}, arg2, {
    promiseFn: arg1
  }) : arg1;
  const promise = options.promise,
        promiseFn = options.promiseFn,
        deferFn = options.deferFn,
        initialValue = options.initialValue,
        onResolve = options.onResolve,
        onReject = options.onReject,
        watch = options.watch,
        watchFn = options.watchFn;

  const _useState = useState({
    data: initialValue instanceof Error ? undefined : initialValue,
    error: initialValue instanceof Error ? initialValue : undefined,
    startedAt: promise || promiseFn ? new Date() : undefined,
    finishedAt: initialValue ? new Date() : undefined
  }),
        _useState2 = _slicedToArray(_useState, 2),
        state = _useState2[0],
        setState = _useState2[1];

  const handleData = function handleData(data) {
    let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => {};

    if (isMounted.current) {
      setState(state => _objectSpread$1({}, state, {
        data,
        error: undefined,
        finishedAt: new Date()
      }));
      callback(data);
    }

    return data;
  };

  const handleError = function handleError(error) {
    let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => {};

    if (isMounted.current) {
      setState(state => _objectSpread$1({}, state, {
        error,
        finishedAt: new Date()
      }));
      callback(error);
    }

    return error;
  };

  const handleResolve = count => data => count === counter.current && handleData(data, onResolve);

  const handleReject = count => error => count === counter.current && handleError(error, onReject);

  const start = () => {
    if ("AbortController" in window) {
      abortController.current.abort();
      abortController.current = new window.AbortController();
    }

    counter.current++;
    setState(state => _objectSpread$1({}, state, {
      startedAt: new Date(),
      finishedAt: undefined
    }));
  };

  const load = () => {
    if (promise) {
      start();
      return promise.then(handleResolve(counter.current), handleReject(counter.current));
    }

    const isPreInitialized = initialValue && counter.current === 0;

    if (promiseFn && !isPreInitialized) {
      start();
      return promiseFn(options, abortController.current).then(handleResolve(counter.current), handleReject(counter.current));
    }
  };

  const run = function run() {
    if (deferFn) {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      lastArgs.current = args;
      start();
      return deferFn(args, options, abortController.current).then(handleResolve(counter.current), handleReject(counter.current));
    }
  };

  const cancel = () => {
    counter.current++;
    abortController.current.abort();
    setState(state => _objectSpread$1({}, state, {
      startedAt: undefined
    }));
  };

  useEffect(() => {
    if (watchFn && prevOptions.current && watchFn(options, prevOptions.current)) load();
  });
  useEffect(() => {
    promise || promiseFn ? load() : cancel();
  }, [promise, promiseFn, watch]);
  useEffect(() => () => isMounted.current = false, []);
  useEffect(() => () => abortController.current.abort(), []);
  useEffect(() => (prevOptions.current = options) && undefined);
  useDebugValue(state, (_ref2) => {
    let startedAt = _ref2.startedAt,
        finishedAt = _ref2.finishedAt,
        error = _ref2.error;
    if (startedAt && (!finishedAt || finishedAt < startedAt)) return `[${counter.current}] Loading`;
    if (finishedAt) return error ? `[${counter.current}] Rejected` : `[${counter.current}] Resolved`;
    return `[${counter.current}] Pending`;
  });
  return useMemo(() => _objectSpread$1({}, state, {
    initialValue,
    isLoading: state.startedAt && (!state.finishedAt || state.finishedAt < state.startedAt),
    counter: counter.current,
    run,
    reload: () => lastArgs.current ? run(...lastArgs.current) : load(),
    cancel,
    setData: handleData,
    setError: handleError
  }), [state]);
};

const parseResponse = (accept, json) => res => {
  if (!res.ok) return Promise.reject(res);
  if (json === false) return res;
  if (json === true || accept === "application/json") return res.json();
  return res;
};

const useAsyncFetch = function useAsyncFetch(input, init) {
  let _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  let defer = _ref.defer,
      json = _ref.json,
      options = _objectWithoutProperties(_ref, ["defer", "json"]);

  const method = input.method || init && init.method;
  const headers = input.headers || init && init.headers || {};
  const accept = headers["Accept"] || headers["accept"] || headers.get && headers.get("accept");

  const doFetch = (input, init) => window.fetch(input, init).then(parseResponse(accept, json));

  const isDefer = defer === true || ~["POST", "PUT", "PATCH", "DELETE"].indexOf(method);
  const fn = defer === false || !isDefer ? "promiseFn" : "deferFn";
  const state = useAsync(_objectSpread$1({}, options, {
    [fn]: useCallback((_, props, ctrl) => doFetch(input, _objectSpread$1({
      signal: ctrl ? ctrl.signal : props.signal
    }, init)), [JSON.stringify(input), JSON.stringify(init)])
  }));
  useDebugValue(state, (_ref3) => {
    let startedAt = _ref3.startedAt,
        finishedAt = _ref3.finishedAt,
        error = _ref3.error,
        counter = _ref3.counter;
    if (startedAt && (!finishedAt || finishedAt < startedAt)) return `[${counter}] Loading`;
    if (finishedAt) return error ? `[${counter}] Rejected` : `[${counter}] Resolved`;
    return `[${counter}] Pending`;
  });
  return state;
};

const unsupported = () => {
  throw new Error("useAsync requires React v16.8 or up. Upgrade your React version or use the <Async> component instead.");
};

var useAsync$1 = useState ? useAsync : unsupported;
const useFetch = useState ? useAsyncFetch : unsupported;

export default Async;
export { createInstance, useAsync$1 as useAsync, useFetch };
